import { useEffect } from 'react';
import { useConfig, useDispatch, useMachineState } from '../app/context';
import { cx } from '../components/cx';
import { PIN_LENGTH } from '../domain/catalog';
import type { PinKey } from '../domain/types';
import common from './common.module.css';
import styles from './LoginScreen.module.css';

const KEYS: readonly PinKey[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'CLR', '0', 'DEL'];
const KEY_LABELS: Partial<Record<PinKey, string>> = { CLR: 'Clear', DEL: 'Delete last digit' };

/** Maps a physical key to a keypad key, so the PIN can also be typed on a keyboard. */
function keyFromKeyboard(key: string): PinKey | null {
  if (/^[0-9]$/.test(key)) return key as PinKey;
  if (key === 'Backspace') return 'DEL';
  if (key === 'Escape' || key === 'Delete') return 'CLR';
  return null;
}

export function LoginScreen() {
  const { pin, pinStatus, pinRetryAfter, operator } = useMachineState();
  const { demoMode, demoPin } = useConfig();
  const dispatch = useDispatch();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const key = keyFromKeyboard(e.key);
      if (!key) return;
      e.preventDefault();
      dispatch({ type: 'PIN_KEY', key });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dispatch]);

  const granted = pinStatus === 'granted';
  const denied = pinStatus === 'denied';
  const feedback =
    granted && operator
      ? { text: `ACCESS GRANTED — ${operator.name}`, className: styles.feedbackGranted }
      : denied
        ? { text: 'ACCESS DENIED — INVALID CODE', className: styles.feedbackDenied }
        : pinStatus === 'unavailable'
          ? {
              text: 'CANNOT VERIFY CODE — CHECK CONNECTION AND TRY AGAIN',
              className: styles.feedbackUnavailable,
            }
          : pinStatus === 'locked'
            ? {
                text: `TOO MANY ATTEMPTS — UNIT LOCKED FOR ${Math.ceil((pinRetryAfter ?? 900) / 60)} MIN`,
                className: styles.feedbackUnavailable,
              }
            : null;

  return (
    <section
      className={cx(common.screen, styles.screen, granted && styles.leaving)}
      aria-label="Operator authentication"
    >
      <div className={styles.intro}>
        <div className={cx(common.kicker, common.reveal)}>OPERATOR AUTHENTICATION</div>
        <h2 className={styles.heading}>Enter Access Code</h2>
        <div className={styles.subheading}>
          Enter your {PIN_LENGTH}-digit PIN to start the robot
        </div>
      </div>

      <div className={styles.pinArea}>
        <div
          className={cx(
            styles.pinBox,
            denied && styles.shake,
            granted && styles.granted,
            (denied || pinStatus === 'unavailable' || pinStatus === 'locked') && styles.denied,
          )}
          role="img"
          aria-label={`${pin.length} of ${PIN_LENGTH} digits entered`}
        >
          {Array.from({ length: PIN_LENGTH }, (_, i) => (
            <span key={i} className={cx(styles.pinDot, i < pin.length && styles.filled)} />
          ))}
          {granted && <div className={styles.grantedRing} />}
        </div>
        {feedback && (
          <div className={cx(styles.feedback, feedback.className)} aria-hidden>
            {feedback.text}
          </div>
        )}
      </div>

      {/* Always mounted so changes are announced; visually hidden and out of the flex flow. */}
      <div className="visually-hidden" role="status" aria-live="assertive">
        {feedback?.text}
      </div>

      <div className={styles.keypad}>
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className={cx(styles.key, key.length > 1 && styles.keyCommand)}
            onClick={() => dispatch({ type: 'PIN_KEY', key })}
            aria-label={KEY_LABELS[key]}
          >
            {key}
          </button>
        ))}
      </div>

      {demoMode && <div className={styles.hint}>DEMO BUILD — ACCESS CODE {demoPin}</div>}
    </section>
  );
}
